import { Button } from '@/components/common/button'
import { Card } from '@/components/common/card'
import { TokenImage } from '@/components/common/tokenImage'
import { UtilizationTooltip } from '@/components/common/utilizationTooltip'
import { routes } from '@/config/routes'
import { useMixpanel } from '@/context/mixpanel'
import type { BorrowMarketRow } from '@/hooks/useBorrowMarkets'
import { cn } from '@/utils/cn'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { formatDecimalToPercentage, formatUSD } from '@vaultsfyi/common/utils'
import type { ReactNode } from 'react'
import { Link, generatePath } from 'react-router'
import { NetworkImage } from '../common/networkImage'
import { SearchIcon } from '../icons/search'
import { BorrowMarketProtocolTooltip } from './borrowMarketProtocolTooltip'
import { BorrowMarketsCollateral } from './borrowMarketsCollateral'
import { BorrowMarketsPagination } from './borrowMarketsPagination'

interface BorrowMarketsCardsProps {
  borrowMarkets: BorrowMarketRow[]
  currentPage: number
  itemsCount: number
  itemsPerPage: number
  className?: string
}

export const BorrowMarketsCards = ({
  borrowMarkets,
  currentPage,
  itemsCount,
  itemsPerPage,
  className,
}: BorrowMarketsCardsProps) => {
  const { trackEvent } = useMixpanel()

  if (borrowMarkets.length === 0) {
    return (
      <Card className={cn('flex w-full flex-col items-center justify-center gap-4 py-4', className)}>
        <SearchIcon size={48} />
        <p className="font-bold text-xl">No results for selected filters</p>
      </Card>
    )
  }

  return (
    <section className={cn('flex w-full flex-col gap-3', className)}>
      <div className="flex flex-col gap-2">
        {borrowMarkets.map(({ id, market, assetData, collateralAssets }) => (
          <Card
            key={id}
            as="article"
            className={cn('group relative flex flex-col gap-2 px-4 pt-4 pb-3', 'ring-inset has-[:focus-visible]:ring')}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <TokenImage symbol={assetData.asset.symbol} className="size-7 shrink-0 p-0.5" />
                <div className="flex min-w-0 flex-col">
                  <div className="flex min-w-0 items-baseline gap-1.5">
                    <Button
                      variant="default"
                      size="default"
                      className={cn(
                        '!ring-0 min-w-0 shrink-0 text-heading-foreground text-lg',
                        'before:absolute before:inset-0 before:h-full before:w-full'
                      )}
                      asChild
                    >
                      <Link
                        to={generatePath(routes.borrowMarket, {
                          network: market.network.name,
                          marketId: market.marketId,
                          assetId: assetData.marketAssetId,
                        })}
                        prefetch="viewport"
                        onClick={() =>
                          trackEvent('Borrow market card clicked', {
                            asset: assetData.asset.symbol,
                            network: market.network.name,
                            marketId: market.marketId,
                          })
                        }
                      >
                        <h3 className="font-medium text-sm" title={`${assetData.asset.symbol} borrow market`}>
                          <span className="sr-only">Go to</span> {assetData.asset.symbol}{' '}
                          <span className="sr-only">borrow market page</span>
                        </h3>
                      </Link>
                    </Button>
                    <p className="min-w-0 truncate text-body-foreground text-xs">{market.name}</p>
                  </div>
                  <div className="flex min-w-0 max-w-full items-center gap-1 overflow-hidden">
                    <p className="truncate text-xs capitalize">{getNetworkName(market.network.name)}</p>
                    <NetworkImage network={market.network.name} className="size-4 shrink-0 rounded-full" />
                  </div>
                </div>
              </div>
              <BorrowMarketProtocolTooltip protocol={market.protocol} className="size-8 shrink-0 p-1" />
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-body-foreground text-sm">Collateral</span>
              <BorrowMarketsCollateral
                assets={collateralAssets}
                className="justify-end"
                iconClassName="size-6 p-0.5"
                maxVisibleAssets={3}
              />
            </div>
            <div className="grid grid-cols-2 xs:grid-cols-4 gap-x-1 gap-y-2 text-sm">
              <Metric
                label="Market size"
                value={assetData.totalSupplied?.usd ? formatUSD(assetData.totalSupplied.usd) : '--'}
              />
              <Metric
                label="Available to Borrow"
                value={assetData.availableToBorrow?.usd ? formatUSD(assetData.availableToBorrow.usd) : '--'}
                className="xs:items-start items-end xs:text-left text-right"
              />
              <Metric
                label="Utilization"
                value={
                  assetData.utilizationRate !== undefined
                    ? formatDecimalToPercentage(assetData.utilizationRate, { fractionDigits: 0 })
                    : '--'
                }
                prefix={
                  assetData.totalSupplied?.native && assetData.availableLiquidity?.native ? (
                    <UtilizationTooltip
                      totalSupplied={assetData.totalSupplied.native}
                      availableLiquidity={assetData.availableLiquidity.native}
                      utilizationRate={assetData.utilizationRate}
                      size={16}
                      className="min-h-4 min-w-4"
                    />
                  ) : undefined
                }
                className="xs:items-end xs:text-right"
              />
              <Metric
                label="Borrow rate"
                value={assetData.borrowRate !== undefined ? formatDecimalToPercentage(assetData.borrowRate) : '--'}
                className="items-end text-right"
              />
            </div>
          </Card>
        ))}
      </div>
      {itemsCount > 0 && (
        <BorrowMarketsPagination currentPage={currentPage} itemsCount={itemsCount} itemsPerPage={itemsPerPage} />
      )}
    </section>
  )
}

interface MetricProps {
  label: string
  value: string
  prefix?: ReactNode
  className?: string
}

const Metric = ({ label, value, prefix, className }: MetricProps) => (
  <div className={cn('flex min-w-0 flex-col gap-0.5', className)}>
    <span className="truncate text-body-foreground text-xs">{label}</span>
    <span className="flex max-w-full items-center gap-1.5 font-medium text-heading-foreground text-sm tabular-nums">
      {prefix}
      <span className="truncate">{value}</span>
    </span>
  </div>
)
